import { useCallback, useEffect, useRef, useState } from "react";

/** Idle timeout in milliseconds. PCI DSS 4.0 Req 8.2.8 mandates
 *  re-authentication after at most 15 minutes of inactivity. */
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

function dbg(msg: string): void {
  console.debug(`[inactivity-lock] ${new Date().toLocaleTimeString()} — ${msg}`);
}

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "touchstart", "scroll"] as const;

const LOCK_KEY = "tilt-pos.locked";

function readPersistedLock(): boolean {
  try {
    return window.sessionStorage.getItem(LOCK_KEY) === "true";
  } catch {
    return false;
  }
}

function writePersistedLock(locked: boolean): void {
  try {
    if (locked) window.sessionStorage.setItem(LOCK_KEY, "true");
    else window.sessionStorage.removeItem(LOCK_KEY);
  } catch {
    // sessionStorage unavailable — degrade gracefully
  }
}

export interface UseInactivityLockResult {
  readonly isLocked: boolean;
  readonly lock: () => void;
  readonly unlock: () => void;
}

/**
 * Detects inactivity and locks the session after IDLE_TIMEOUT_MS
 * (15 minutes). The lock state is local to this hook; dismissal is
 * handled by the LockScreen component which calls ``unlock`` on
 * successful PIN verify or re-login.
 *
 * Activity events reset the countdown. The timer is paused while the
 * screen is already locked so a sleeping kiosk doesn't fire repeatedly.
 */
export function useInactivityLock(): UseInactivityLockResult {
  const [isLocked, setIsLocked] = useState<boolean>(() => {
    const persisted = readPersistedLock();
    dbg(`init — persisted=${String(persisted)}`);
    return persisted;
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    const lockAt = new Date(Date.now() + IDLE_TIMEOUT_MS).toLocaleTimeString();
    dbg(`timer reset — will lock at ${lockAt}`);
    timerRef.current = setTimeout(() => {
      dbg("idle timeout reached — locking");
      writePersistedLock(true);
      setIsLocked(true);
    }, IDLE_TIMEOUT_MS);
  }, []);

  const lock = useCallback(() => {
    dbg("manual lock");
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    writePersistedLock(true);
    setIsLocked(true);
  }, []);

  const unlock = useCallback(() => {
    dbg("unlock — restarting timer");
    writePersistedLock(false);
    setIsLocked(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    resetTimer();

    function onActivity() {
      // Don't reset the countdown while locked - that would extend the
      // lockout window if someone is typing on the lock screen.
      setIsLocked((locked) => {
        if (!locked) resetTimer();
        return locked;
      });
    }

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
    };
  }, [resetTimer]);

  return { isLocked, lock, unlock };
}
